import type { Layer } from '../music/figuration';
import type { PunctuationMark, RegionRole } from '../music/piece';
import type { PathFamily } from '../data/types';
import type { Depth } from './hooks';

export const APP_NAME = 'Gravity Atlas';
export const TAGLINE = 'Listen first. See second. Name last.';

/** How much theory the pages show. `short` names the level in the header; `long` explains it in settings. */
export const DEPTH_LABELS: Record<Depth, { short: string; long: string }> = {
  0: { short: 'Just listen', long: 'Sound and shape only, almost no words.' },
  1: { short: 'Plain words', long: 'What you hear, in everyday language.' },
  2: { short: 'Names', long: 'The names of cadences, patterns and keys.' },
  3: { short: 'Symbols', long: 'Roman numerals or figured bass under the music.' },
  4: { short: 'Full theory', long: 'Explanations, sources and how tension is computed.' },
};

export const REGION_WORDS: Record<RegionRole, string> = {
  home: 'home',
  away: 'away',
  shadow: 'shadow',
  return: 'home again',
};

export const PUNCTUATION: Record<PunctuationMark, { glyph: string; name: string; abbr: string; tense: boolean }> = {
  full: { glyph: '.', name: 'perfect cadence', abbr: 'PAC', tense: false },
  semicolon: { glyph: ';', name: 'imperfect cadence', abbr: 'IAC', tense: false },
  comma: { glyph: ',', name: 'half cadence', abbr: 'HC', tense: true },
  colon: { glyph: ':', name: 'on the dominant', abbr: 'V pedal', tense: true },
  ellipsis: { glyph: '…', name: 'deceptive cadence', abbr: 'DC', tense: true },
};

export const LAYER_LABELS: Record<Layer, { label: string; hint: string }> = {
  surface: { label: 'As written', hint: 'Every note of the score' },
  chords: { label: 'Chords only', hint: 'The figuration folded into block chords' },
  frame: { label: 'Melody + bass', hint: 'The top voice and the bass only' },
  ground: { label: 'Bass only', hint: 'The bass line alone' },
  plan: { label: 'Key centres', hint: 'Only the home note of each passage' },
};

export const FAMILY_LABELS: Record<PathFamily, string> = {
  cadence: 'Punctuation',
  opening: 'Openings',
  answer: 'Answers',
  sequence: 'Sequences',
  pedal: 'Held basses',
  prolongation: 'Staying home',
  colour: 'Changing light',
  grammar: 'Grammar',
};

/** Plain words for chord families (see chordFamily in data/observatory.ts), used where chords are compared. */
export const CHORD_WORDS: Record<string, string> = {
  I: 'home',
  i: 'home',
  V: 'the pull home',
  IV: 'the soft side',
  iv: 'the soft side',
  ii: 'the approach',
  iio: 'the approach',
  vi: 'the shadow',
  VI: 'the shadow',
  viio: 'the leaning chord',
  iii: 'the mediant',
  III: 'the relative major',
  'V/V': 'the pull to the pull',
  'V/IV': 'leaning to the soft side',
  'V/vi': 'leaning to the shadow',
  'viio/V': 'leaning to the pull',
  v: 'the pull, darkened',
  VII: 'a step below home',
  Cad64: 'the arrival held back',
};

/** A title without the catalogue number it shares with the catalogue line ("Piano Sonata in C major" next to "K. 279 · I"). */
export function shortTitle(title: string, catalogue: string): string {
  const cat = catalogue.split(' · ')[0];
  return cat && title.endsWith(`, ${cat}`) ? title.slice(0, -cat.length - 2) : title;
}
